import {
  ZapIcon,
  LightbulbIcon,
  ToggleLeftIcon,
  RadarIcon,
  ChevronUpIcon,
  RotateCcwIcon,
  WindIcon,
} from 'lucide-react';

// ─── Shared Sensor Type ───────────────────────────────────────────────────────
export type SensorType = 'SEO' | 'SEL' | 'SES' | 'RADAR' | 'SEC' | 'CURTAIN' | 'BLIND' | 'HERV' | 'SER' | 'SEM' | 'SEV';

// ─── Sensor Config (replaces per-file SENSOR_TYPES arrays) ───────────────────
export interface SensorConfig {
  id: SensorType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Space-separated Tailwind classes: "text-X bg-X/20 border-X" */
  color: string;
  /** Individual colour tokens for components needing separate properties */
  colorObject: {
    icon: string;
    bg: string;
    border: string;
    dot: string;
  };
}

import React from 'react';
import { AlignJustifyIcon } from 'lucide-react';

export const SerIcon = (props: React.ComponentProps<typeof ZapIcon>) => {
  const { className, ...rest } = props;
  return React.createElement(
    'div',
    {
      className: `relative inline-flex items-center justify-center ${className || ''}`,
    },
    React.createElement(ZapIcon, {
      className: 'w-full h-full',
      ...rest,
    }),
    React.createElement(
      'span',
      {
        className: 'absolute font-extrabold bg-violet-600 text-white rounded-full flex items-center justify-center shadow-md select-none border border-slate-900',
        style: {
          width: '10px',
          height: '10px',
          bottom: '-3px',
          right: '-4px',
          fontSize: '7px',
          lineHeight: '1',
          padding: '0 0.5px',
        },
      },
      'R'
    )
  );
};

export const SemIcon = (props: React.ComponentProps<typeof ZapIcon>) => {
  const { className, ...rest } = props;
  return React.createElement(
    'div',
    {
      className: `relative inline-flex items-center justify-center ${className || ''}`,
    },
    React.createElement(ZapIcon, {
      className: 'w-full h-full',
      ...rest,
    }),
    React.createElement(
      'span',
      {
        className: 'absolute font-extrabold bg-blue-600 text-white rounded-full flex items-center justify-center shadow-md select-none border border-slate-900',
        style: {
          width: '10px',
          height: '10px',
          bottom: '-3px',
          right: '-4px',
          fontSize: '7px',
          lineHeight: '1',
          padding: '0 0.5px',
        },
      },
      'M'
    )
  );
};

export const SENSOR_CONFIGS: SensorConfig[] = [
  {
    id: 'SEO',
    label: 'Smart Outlet',
    icon: ZapIcon,
    color: 'text-violet-400 bg-violet-500/20 border-violet-400',
    colorObject: {
      icon: 'text-violet-400',
      bg: 'bg-violet-500/20',
      border: 'border-violet-400/30',
      dot: 'bg-violet-400',
    },
  },
  {
    id: 'SER',
    label: 'Smart Relay',
    icon: SerIcon,
    color: 'text-violet-400 bg-violet-500/20 border-violet-400',
    colorObject: {
      icon: 'text-violet-400',
      bg: 'bg-violet-500/20',
      border: 'border-violet-400/30',
      dot: 'bg-violet-400',
    },
  },
  {
    id: 'SEM',
    label: 'Smart Multi Outlet',
    icon: SemIcon,
    color: 'text-blue-400 bg-blue-500/20 border-blue-400',
    colorObject: {
      icon: 'text-blue-400',
      bg: 'bg-blue-500/20',
      border: 'border-blue-400/30',
      dot: 'bg-blue-400',
    },
  },
  {
    id: 'SEL',
    label: 'Smart Light',
    icon: LightbulbIcon,
    color: 'text-amber-400 bg-amber-500/20 border-amber-400',
    colorObject: {
      icon: 'text-amber-400',
      bg: 'bg-amber-500/20',
      border: 'border-amber-400/30',
      dot: 'bg-amber-400',
    },
  },
  {
    id: 'SES',
    label: 'Smart Switch',
    icon: ToggleLeftIcon,
    color: 'text-cyan-400 bg-cyan-500/20 border-cyan-400',
    colorObject: {
      icon: 'text-cyan-400',
      bg: 'bg-cyan-500/20',
      border: 'border-cyan-400/30',
      dot: 'bg-cyan-400',
    },
  },
  {
    id: 'RADAR',
    label: 'Radar Sensor',
    icon: RadarIcon,
    color: 'text-rose-400 bg-rose-500/20 border-rose-400',
    colorObject: {
      icon: 'text-rose-400',
      bg: 'bg-rose-500/20',
      border: 'border-rose-400/30',
      dot: 'bg-rose-400',
    },
  },
  {
    id: 'SEC',
    label: 'Smart Blind',
    icon: AlignJustifyIcon,
    color: 'text-amber-500 bg-amber-500/20 border-amber-500',
    colorObject: {
      icon: 'text-amber-500',
      bg: 'bg-amber-500/20',
      border: 'border-amber-500/30',
      dot: 'bg-amber-500',
    },
  },
  {
    id: 'CURTAIN',
    label: 'Smart Curtain',
    icon: AlignJustifyIcon,
    color: 'text-orange-400 bg-orange-500/20 border-orange-400',
    colorObject: {
      icon: 'text-orange-400',
      bg: 'bg-orange-500/20',
      border: 'border-orange-400/30',
      dot: 'bg-orange-400',
    },
  },
  {
    id: 'BLIND',
    label: 'Smart Blind',
    icon: ChevronUpIcon,
    color: 'text-yellow-500 bg-yellow-500/20 border-yellow-500',
    colorObject: {
      icon: 'text-yellow-500',
      bg: 'bg-yellow-500/20',
      border: 'border-yellow-500/30',
      dot: 'bg-yellow-500',
    },
  },
  {
    id: 'SEV',
    label: '전열교환기',
    icon: WindIcon,
    color: 'text-emerald-400 bg-emerald-500/20 border-emerald-400',
    colorObject: {
      icon: 'text-emerald-400',
      bg: 'bg-emerald-500/20',
      border: 'border-emerald-400/30',
      dot: 'bg-emerald-400',
    },
  },
  {
    id: 'HERV',
    label: 'Ventilation',
    icon: RotateCcwIcon,
    color: 'text-emerald-400 bg-emerald-500/20 border-emerald-400',
    colorObject: {
      icon: 'text-emerald-400',
      bg: 'bg-emerald-500/20',
      border: 'border-emerald-400/30',
      dot: 'bg-emerald-400',
    },
  },
];

const DEFAULT_COLOR = 'text-white bg-white/20 border-white';
const DEFAULT_COLOR_OBJECT = {
  icon: 'text-white',
  bg: 'bg-white/20',
  border: 'border-white/30',
  dot: 'bg-white',
};

/** Returns the Lucide icon component for a given sensor type. */
export function getSensorIcon(
  type: SensorType,
): React.ComponentType<{ className?: string }> {
  return SENSOR_CONFIGS.find((c) => c.id === type)?.icon ?? ZapIcon;
}

/**
 * Returns space-separated Tailwind colour classes for a sensor type.
 * e.g. "text-violet-400 bg-violet-500/20 border-violet-400"
 */
export function getSensorColor(type: SensorType): string {
  return SENSOR_CONFIGS.find((c) => c.id === type)?.color ?? DEFAULT_COLOR;
}

/**
 * Returns a colour object `{ icon, bg, border, dot }` for a sensor type.
 * Used by components that need individual Tailwind class tokens.
 */
export function getSensorColorObject(
  type: SensorType,
): { icon: string; bg: string; border: string; dot: string } {
  return (
    SENSOR_CONFIGS.find((c) => c.id === type)?.colorObject ??
    DEFAULT_COLOR_OBJECT
  );
}

/** Returns the human-readable name for a sensor type. */
export function getSensorTypeName(type: SensorType): string {
  return SENSOR_CONFIGS.find((c) => c.id === type)?.label ?? 'Sensor';
}

/** Returns the friendly localized name for a sensor/device type. */
export function getFriendlyDeviceTypeName(type: string): string {
  switch (type?.toUpperCase()) {
    case 'SEM': return '멀티탭';
    case 'SEO': return '콘센트';
    case 'SES': return '스위치';
    case 'SEL': return '조명';
    case 'SER': return '릴레이';
    case 'RADAR': return '재실센서';
    case 'SEC': return '커튼/블라인드';
    case 'CURTAIN': return '커튼';
    case 'BLIND': return '블라인드';
    case 'HERV':
    case 'SEV': return '전열교환기';
    default: return type || '기기';
  }
}

/**
 * Format ERV telemetry value:
 * 0: 정지, 1: 약풍, 2: 중풍, 3: 강풍, -1: 실패
 */
export function formatErvStatus(rawVal: any): { text: string; color: string } | null {
  if (rawVal === undefined || rawVal === null || rawVal === '') return null;
  const num = Number(rawVal);
  if (isNaN(num)) {
    const str = String(rawVal).trim();
    if (str === '0' || str === '정지' || str === 'off' || str === 'stop') return { text: '정지', color: 'text-slate-500' };
    if (str === '1' || str === '약풍' || str === 'low') return { text: '약풍', color: 'text-emerald-400' };
    if (str === '2' || str === '중풍' || str === 'medium' || str === 'mid') return { text: '중풍', color: 'text-teal-400' };
    if (str === '3' || str === '강풍' || str === 'high') return { text: '강풍', color: 'text-cyan-400' };
    if (str === '-1' || str === '실패' || str === 'error' || str === 'fail') return { text: '실패', color: 'text-rose-400' };
    return { text: str, color: 'text-emerald-400' };
  }
  switch (num) {
    case 0:
      return { text: '정지', color: 'text-slate-500' };
    case 1:
      return { text: '약풍', color: 'text-emerald-400' };
    case 2:
      return { text: '중풍', color: 'text-teal-400' };
    case 3:
      return { text: '강풍', color: 'text-cyan-400' };
    case -1:
      return { text: '실패', color: 'text-rose-400' };
    default:
      return { text: `${num}`, color: 'text-emerald-400' };
  }
}

